// Pure helpers for the "all weapons" shot-count drawer (parts list → graph
// button). The parts API embeds every usage entry on each part (part.usages,
// see backend/app/routes/api/parts/get-part.js), so no extra request is
// needed — these functions only reshape that data into MiniGraph series.
//
// The per-part chart in components/part/view.js does the same maths inline;
// the bucketing rules here (weekly/monthly keys, 12-vs-12 month comparison,
// quarterly ticks) intentionally mirror it so the two views agree.

// Only parts carrying one of these tags count as weapons in the graphs.
export const WEAPON_TAGS = ['pistol', 'rifle'];

// Six clearly different hues; the stroke pattern cycles (solid → dashed →
// dotted) every six series, so up to 18 weapons stay tellable apart.
export const SERIES_COLORS = [
    '#3b82f6', // blue
    '#f97316', // orange
    '#22c55e', // green
    '#ef4444', // red
    '#a855f7', // purple
    '#eab308', // yellow
];

export const SERIES_DASHES = [
    { svg: '', css: 'solid' },
    { svg: '6,4', css: 'dashed' },
    { svg: '2,4', css: 'dotted' },
];

export function seriesColor(idx) {
    return SERIES_COLORS[idx % SERIES_COLORS.length];
}

export function seriesDash(idx) {
    return SERIES_DASHES[Math.floor(idx / SERIES_COLORS.length) % SERIES_DASHES.length];
}

function tagName(tag) {
    const name = tag && typeof tag === 'object' ? tag.name : tag;
    return String(name || '').trim().toLowerCase();
}

/**
 * True when the part is tagged as a weapon (see WEAPON_TAGS). Tags are the
 * `{ name }` objects the parts API embeds, but plain strings work too.
 */
export function isWeaponPart(part) {
    const tags = Array.isArray(part?.tags) ? part.tags : [];
    return tags.some(tag => WEAPON_TAGS.includes(tagName(tag)));
}

/**
 * Dated usage entries with a positive shot count, oldest first.
 * @param {object} part - part object with an optional `usages` array
 * @returns {Array<{ date: Date, shotCount: number }>}
 */
export function shotEntries(part) {
    const usages = Array.isArray(part?.usages) ? part.usages : [];
    return usages
        .filter(usage => usage && usage.shotCount > 0 && (usage.startDate || usage.date))
        .map(usage => ({
            date: new Date(usage.startDate || usage.date),
            shotCount: usage.shotCount,
        }))
        .filter(entry => !Number.isNaN(entry.date.getTime()))
        .sort((a, b) => a.date - b.date);
}

export function totalShots(entries) {
    return (entries || []).reduce((acc, entry) => acc + (entry.shotCount || 0), 0);
}

// Period keys offered by the drawer, in display order. 'all' = no lower bound.
export const PERIODS = ['month', '3m', '12m', 'year', 'all'];

/**
 * Lower/upper bound for a period key. `start` is null for 'all'; `end` is
 * always `now` so series can be extended to the right edge of the chart.
 * @returns {{ start: Date|null, end: Date }}
 */
export function periodRange(period, now = new Date()) {
    const end = new Date(now);
    let start = null;
    if (period === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (period === 'year') {
        start = new Date(now.getFullYear(), 0, 1);
    } else if (period === '3m' || period === '12m') {
        start = new Date(now);
        start.setMonth(start.getMonth() - (period === '3m' ? 3 : 12));
        start.setHours(0, 0, 0, 0);
    }
    return { start, end };
}

export function filterEntries(entries, range) {
    const start = range?.start;
    if (!start) {
        return entries || [];
    }
    return (entries || []).filter(entry => entry.date >= start);
}

/**
 * Running total over time, in the {epoch, x, y} shape MiniGraph expects.
 * With a range, the line starts at 0 on `range.start` and is extended flat
 * to `range.end` so every weapon spans the same x interval.
 */
export function cumulativeSeries(entries, range) {
    if (!entries || entries.length === 0) {
        return [];
    }
    let cumulative = 0;
    const series = entries.map(entry => {
        cumulative += entry.shotCount;
        const epoch = entry.date.getTime();
        return { epoch, x: epoch, y: cumulative };
    });
    if (range?.start && range.start.getTime() < series[0].x) {
        const epoch = range.start.getTime();
        series.unshift({ epoch, x: epoch, y: 0 });
    }
    if (range?.end && range.end.getTime() > series[series.length - 1].x) {
        const epoch = range.end.getTime();
        series.push({ epoch, x: epoch, y: cumulative });
    }
    return series;
}

/**
 * Local-time Monday 00:00 of the week containing `date`, as an epoch.
 */
export function weekStartEpoch(date) {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - ((day + 6) % 7));
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

/**
 * Shots per week from the week of the first entry (or of `start`, when
 * given) through the current week (or the last entry, if later). Weeks
 * without shots are present with y = 0, so a line drops to zero instead of
 * bridging idle periods.
 */
export function weeklySeries(entries, now = new Date(), start = null) {
    if (!entries || entries.length === 0) {
        return [];
    }
    const buckets = {};
    entries.forEach(entry => {
        const key = weekStartEpoch(entry.date);
        buckets[key] = (buckets[key] || 0) + entry.shotCount;
    });
    const weekKeys = Object.keys(buckets).map(key => parseInt(key, 10));
    const firstWeek = Math.min(...weekKeys, ...(start ? [weekStartEpoch(start)] : []));
    const lastWeek = Math.max(weekStartEpoch(now), ...weekKeys);
    const series = [];
    const cursor = new Date(firstWeek);
    while (cursor.getTime() <= lastWeek) {
        const key = cursor.getTime();
        series.push({ epoch: key, x: key, y: buckets[key] || 0 });
        cursor.setDate(cursor.getDate() + 7);
    }
    return series;
}

/**
 * X-axis ticks every third month from the first month of data, plus one more
 * quarter when that quarter has already started.
 */
export function quarterlyTicks(firstDate, lastDate, now = new Date()) {
    if (!firstDate || !lastDate) {
        return [];
    }
    const ticks = [];
    const current = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
    while (current <= lastDate) {
        ticks.push(current.getTime());
        current.setMonth(current.getMonth() + 3);
    }
    if (current <= now) {
        ticks.push(current.getTime());
    }
    return ticks;
}

function monthStartEpoch(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * X-axis ticks whose density follows the span: every Monday for up to two
 * months, every month up to about a year, otherwise every quarter.
 * @returns {{ ticks: number[], unit: 'week'|'month'|'quarter' }}
 */
export function axisTicks(firstDate, lastDate) {
    if (!firstDate || !lastDate) {
        return { ticks: [], unit: 'quarter' };
    }
    const spanDays = (lastDate.getTime() - firstDate.getTime()) / DAY_MS;
    if (spanDays <= 62) {
        const ticks = [];
        const cursor = new Date(weekStartEpoch(firstDate));
        while (cursor <= lastDate) {
            ticks.push(cursor.getTime());
            cursor.setDate(cursor.getDate() + 7);
        }
        return { ticks, unit: 'week' };
    }
    if (spanDays <= 400) {
        const ticks = [];
        const cursor = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
        while (cursor <= lastDate) {
            ticks.push(cursor.getTime());
            cursor.setMonth(cursor.getMonth() + 1);
        }
        return { ticks, unit: 'month' };
    }
    return { ticks: quarterlyTicks(firstDate, lastDate, lastDate), unit: 'quarter' };
}

function sortedMonthSeries(buckets) {
    return Object.keys(buckets)
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
        .map(key => ({ x: parseInt(key, 10), y: buckets[key] }));
}

/**
 * Monthly totals for the last 12 months, and for the 12 months before that
 * shifted forward one year so both series share the same x positions.
 */
export function monthlyComparison(entries, now = new Date()) {
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    const twentyFourMonthsAgo = new Date(now);
    twentyFourMonthsAgo.setMonth(twentyFourMonthsAgo.getMonth() - 24);

    const current = {};
    const previous = {};
    (entries || []).forEach(entry => {
        if (entry.date >= twelveMonthsAgo) {
            const key = monthStartEpoch(entry.date);
            current[key] = (current[key] || 0) + entry.shotCount;
        } else if (entry.date >= twentyFourMonthsAgo) {
            const shifted = new Date(entry.date);
            shifted.setFullYear(shifted.getFullYear() + 1);
            const key = monthStartEpoch(shifted);
            previous[key] = (previous[key] || 0) + entry.shotCount;
        }
    });

    const comparisonTicks = [];
    const tick = new Date(twelveMonthsAgo.getFullYear(), twelveMonthsAgo.getMonth(), 1);
    while (tick <= now) {
        comparisonTicks.push(tick.getTime());
        tick.setMonth(tick.getMonth() + 1);
    }

    return {
        currentYearData: sortedMonthSeries(current),
        previousYearData: sortedMonthSeries(previous),
        comparisonTicks,
    };
}

/**
 * One row per weapon part (see isWeaponPart) that has logged shots inside
 * the period, most used first. Totals, last-used and series only count
 * entries inside the period.
 * @returns {Array<{ id, name, total, lastUsed: Date, entries, cumulative, weekly, color, dash }>}
 */
export function weaponUsageSummary(parts, now = new Date(), period = 'all') {
    const range = periodRange(period, now);
    const rows = (parts || [])
        .filter(isWeaponPart)
        .map(part => {
            const entries = filterEntries(shotEntries(part), range);
            return {
                id: part.id,
                name: part.name || '',
                total: totalShots(entries),
                lastUsed: entries.length > 0 ? entries[entries.length - 1].date : null,
                entries,
                cumulative: cumulativeSeries(entries, range),
                weekly: weeklySeries(entries, now, range.start),
            };
        })
        .filter(row => row.total > 0)
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

    return rows.map((row, idx) => ({ ...row, color: seriesColor(idx), dash: seriesDash(idx) }));
}

/**
 * Everything the drawer renders: per-weapon rows for the period, combined
 * totals, x ticks spanning the period, and the 12-vs-12 month comparison
 * (which is period-independent by nature). Parts without a weapon tag are
 * ignored entirely.
 */
export function allWeaponsStats(parts, now = new Date(), period = 'all') {
    const range = periodRange(period, now);
    const weapons = weaponUsageSummary(parts, now, period);
    const allEntries = weapons
        .reduce((acc, weapon) => acc.concat(weapon.entries), [])
        .sort((a, b) => a.date - b.date);
    const firstDate = allEntries.length > 0 ? allEntries[0].date : null;
    const lastDate = allEntries.length > 0 ? allEntries[allEntries.length - 1].date : null;
    const axis = axisTicks(range.start || firstDate, firstDate ? range.end : null);

    // The comparison always covers the last 24 months, whatever the period.
    const comparisonEntries = (parts || [])
        .filter(isWeaponPart)
        .reduce((acc, part) => acc.concat(shotEntries(part)), [])
        .sort((a, b) => a.date - b.date);

    return {
        period,
        range,
        weapons,
        total: totalShots(allEntries),
        firstDate,
        lastDate,
        ticks: axis.ticks,
        tickUnit: axis.unit,
        comparison: monthlyComparison(comparisonEntries, now),
    };
}
